import { CommonModule } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  first,
  map,
  shareReplay,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { SortChangeEvent } from 'src/app/core/directives/sortable-header.directive';
import {
  InventoryItemType,
  getInventoryItemTypeLabelKey,
  isInventoryItemType,
} from 'src/app/core/enums/inventory-item-type.enum';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { Filter, RequestModel } from 'src/app/core/models/request-model.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderOverview } from 'src/app/core/models/sales/company-sales-order-overview.interface';
import {
  SalesOrderListItemView,
  SalesOrderSource,
} from 'src/app/core/models/sales/sales-order-list-item-view.interface';
import {
  CrmColumnVisibilityMap,
  CrmViewMode,
  CrmViewPreferencesService,
} from 'src/app/core/services/crm-view-preferences.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { SalesOrderService } from 'src/app/core/services/sales-order.service';
import { CreateSalesOrderComponent } from './create-sales-order/create-sales-order.component';
import { getSalesOrderStatusLabelKey, normalizeSalesOrderStatus } from './sales-order-status-label.util';
import {
  SALES_ORDERS_FILTER_FIELDS,
  SALES_ORDERS_STATUS_OPTIONS,
  SALES_ORDERS_TYPE_OPTIONS,
  SalesOrdersFilterModel,
  SalesOrdersSourceFilter,
  SalesOrdersStatusFilter,
  SalesOrdersStatusOption,
  SalesOrdersTypeFilter,
  createEmptySalesOrdersFilterModel,
} from './sales-orders-filter.view-model';
import {
  SALES_ORDERS_TABLE_COLUMNS,
  SalesOrdersTableColumnDescriptor,
  SalesOrdersTableColumnKey,
  SalesOrdersTableColumnVisibility,
  buildDefaultSalesOrdersColumnVisibility,
  extractPersistableSalesOrdersVisibility,
  mergeSalesOrdersColumnVisibility,
} from './sales-orders-table-columns';

type SalesOrderRowActionId =
  | 'open'
  | 'issue-invoice'
  | 'create-shipment'
  | 'duplicate'
  | 'archive'
  | 'delete';

@Component({
  selector: 'app-sales-orders',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, CoreModule, CreateSalesOrderComponent],
  templateUrl: './sales-orders.component.html',
  styleUrl: './sales-orders.component.scss',
})
export class SalesOrdersComponent {
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly permissionService = inject(PermissionService);
  private readonly viewPreferencesService = inject(CrmViewPreferencesService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  public readonly emptyListParam: Record<string, string> = {
    uk: 'замовлень на продаж',
    en: 'sales orders',
  };

  public readonly typeOptions: readonly InventoryItemType[] = SALES_ORDERS_TYPE_OPTIONS;
  public readonly statusOptions: readonly SalesOrdersStatusOption[] = SALES_ORDERS_STATUS_OPTIONS;
  public readonly columns: readonly SalesOrdersTableColumnDescriptor[] = SALES_ORDERS_TABLE_COLUMNS;

  public companyId = '';
  public isLoading = false;
  public isCreateSalesOrderModalOpen = false;
  public errorMessageKey = '';
  public rowActionErrorKey = '';
  public searchTerm = '';
  public currentLang = 'en';

  public filterModel: SalesOrdersFilterModel = createEmptySalesOrdersFilterModel();
  public activeFilterCount = 0;
  public viewMode: CrmViewMode = 'table';
  public visibleColumns: SalesOrdersTableColumnVisibility =
    buildDefaultSalesOrdersColumnVisibility();
  public isColumnMenuOpen = false;

  public salesOrders$: Observable<PagedList<SalesOrderListItemView>> = EMPTY;
  public readonly canCreateSalesOrder$ = this.permissionService.userPermissions$.pipe(
    map((permissions) =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Company,
        UserCompanyPermissions.Edit,
      ),
    ),
    shareReplay(1),
  );

  public isDeleteConfirmationOpen = false;
  public isArchiveConfirmationOpen = false;
  public isRowActionInFlight = false;
  public pendingDeleteOrder: SalesOrderListItemView | null = null;
  public pendingArchiveOrder: SalesOrderListItemView | null = null;

  private readonly searchChanges$ = new Subject<string>();
  private request: RequestModel = {
    page: 1,
    pageSize: 10,
    filters: [],
    sortColumn: 'createdAt',
    sortOrder: 'desc',
  };

  constructor() {
    const route = inject(ActivatedRoute);

    route.parent?.parent?.params
      .pipe(
        first(),
        tap((params) => {
          this.companyId = params['id'];
          this.currentLang = this.translateService.currentLang === 'uk' ? 'uk' : 'en';
          this.viewMode = this.readPersistedViewMode(this.companyId);
          this.visibleColumns = this.readPersistedColumnVisibility(this.companyId);
          this.isLoading = true;
          this.loadSalesOrders();
        }),
      )
      .subscribe();

    this.searchChanges$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          this.request.page = 1;
          this.request.searchTerm = value.trim() || undefined;
          this.isLoading = true;
          this.loadSalesOrders();
        }),
      )
      .subscribe();
  }

  public openCreateSalesOrderModal(): void {
    this.isCreateSalesOrderModalOpen = true;
  }

  public closeCreateSalesOrderModal(): void {
    this.isCreateSalesOrderModalOpen = false;
  }

  public onSalesOrderCreated(salesOrder: CompanySalesOrderDetails): void {
    this.closeCreateSalesOrderModal();
    void this.router.navigate(['/companies', this.companyId, 'crm', 'sales-orders', salesOrder.id]);
  }

  public retry(): void {
    this.isLoading = true;
    this.loadSalesOrders();
  }

  public onPageChange(page: number): void {
    this.request.page = page;
    this.loadSalesOrders();
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;
    this.request.page = 1;
    this.loadSalesOrders();
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChanges$.next(value);
  }

  public onSortChange(event: SortChangeEvent): void {
    this.request.sortColumn = event.column;
    this.request.sortOrder = event.order;
    this.request.page = 1;
    this.isLoading = true;
    this.loadSalesOrders();
  }

  public get activeSortColumn(): string {
    return this.request.sortColumn ?? '';
  }

  public get activeSortOrder(): 'asc' | 'desc' {
    return this.request.sortOrder ?? 'asc';
  }

  public onStatusFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      statusFilter: this.coerceStatusFilter(value),
    };
    this.applyFilters();
  }

  public onTypeFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      typeFilter: this.coerceTypeFilter(value),
    };
    this.applyFilters();
  }

  public onSourceFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      sourceFilter: this.coerceSourceFilter(value),
    };
    this.applyFilters();
  }

  public resetFilters(): void {
    this.filterModel = createEmptySalesOrdersFilterModel();
    this.applyFilters();
  }

  public setViewMode(mode: CrmViewMode): void {
    if (this.viewMode === mode) {
      return;
    }
    this.viewMode = mode;
    if (this.companyId) {
      try {
        this.viewPreferencesService.setSalesOrderViewMode(this.companyId, mode);
      } catch {
        // Storage may be unavailable; keep the in-memory mode change.
      }
    }
  }

  public toggleColumnMenu(): void {
    this.isColumnMenuOpen = !this.isColumnMenuOpen;
  }

  public closeColumnMenu(): void {
    this.isColumnMenuOpen = false;
  }

  public toggleColumn(key: SalesOrdersTableColumnKey): void {
    const descriptor = this.columns.find((column) => column.key === key);
    if (!descriptor || descriptor.locked) {
      return;
    }

    this.visibleColumns = {
      ...this.visibleColumns,
      [key]: !this.visibleColumns[key],
    };

    if (!this.companyId) {
      return;
    }

    try {
      this.viewPreferencesService.setSalesOrderColumnVisibility(
        this.companyId,
        extractPersistableSalesOrdersVisibility(this.visibleColumns),
      );
    } catch {
      // Storage may be unavailable; keep the in-memory change.
    }
  }

  @HostListener('document:keydown.escape')
  public onEscapeKey(): void {
    if (this.isColumnMenuOpen) {
      this.isColumnMenuOpen = false;
    }
  }

  public openSalesOrder(salesOrderId: string): void {
    void this.router.navigate(['/companies', this.companyId, 'crm', 'sales-orders', salesOrderId]);
  }

  public onRowAction(actionId: string, salesOrder: SalesOrderListItemView): void {
    const action = actionId as SalesOrderRowActionId;
    switch (action) {
      case 'open':
        void this.router.navigate(['/companies', this.companyId, 'crm', 'sales-orders', salesOrder.id]);
        return;
      case 'issue-invoice':
        this.openOrderWithQuickAction(salesOrder, 'invoice');
        return;
      case 'create-shipment':
        this.openOrderWithQuickAction(salesOrder, 'shipment');
        return;
      case 'duplicate':
        this.duplicateSalesOrder(salesOrder);
        return;
      case 'archive':
        this.requestArchive(salesOrder);
        return;
      case 'delete':
        this.requestDelete(salesOrder);
        return;
      default:
        return;
    }
  }

  public closeArchiveConfirmation(): void {
    if (this.isRowActionInFlight) {
      return;
    }
    this.isArchiveConfirmationOpen = false;
    this.pendingArchiveOrder = null;
  }

  public confirmArchive(): void {
    if (this.isRowActionInFlight || !this.pendingArchiveOrder) {
      return;
    }

    const target = this.pendingArchiveOrder;
    this.isRowActionInFlight = true;
    this.rowActionErrorKey = '';

    this.salesOrderService
      .archiveSalesOrder(this.companyId, target.id)
      .pipe(
        first(),
        tap(() => {
          this.isArchiveConfirmationOpen = false;
          this.pendingArchiveOrder = null;
          this.isLoading = true;
          this.loadSalesOrders();
        }),
        catchError(() => {
          this.rowActionErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.ARCHIVE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isRowActionInFlight = false;
        }),
      )
      .subscribe();
  }

  public closeDeleteConfirmation(): void {
    if (this.isRowActionInFlight) {
      return;
    }
    this.isDeleteConfirmationOpen = false;
    this.pendingDeleteOrder = null;
  }

  public confirmDelete(): void {
    if (this.isRowActionInFlight || !this.pendingDeleteOrder) {
      return;
    }

    const target = this.pendingDeleteOrder;
    this.isRowActionInFlight = true;
    this.rowActionErrorKey = '';

    this.salesOrderService
      .deleteSalesOrder(this.companyId, target.id)
      .pipe(
        first(),
        tap(() => {
          this.isDeleteConfirmationOpen = false;
          this.pendingDeleteOrder = null;
          this.isLoading = true;
          this.loadSalesOrders();
        }),
        catchError(() => {
          this.rowActionErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.DELETE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isRowActionInFlight = false;
        }),
      )
      .subscribe();
  }

  private duplicateSalesOrder(salesOrder: SalesOrderListItemView): void {
    if (!salesOrder.canDuplicate || this.isRowActionInFlight) {
      return;
    }

    this.isRowActionInFlight = true;
    this.rowActionErrorKey = '';

    this.salesOrderService
      .duplicateSalesOrder(this.companyId, salesOrder.id)
      .pipe(
        first(),
        tap((created) => {
          void this.router.navigate(['/companies', this.companyId, 'crm', 'sales-orders', created.id]);
        }),
        catchError(() => {
          this.rowActionErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.DUPLICATE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isRowActionInFlight = false;
        }),
      )
      .subscribe();
  }

  private requestDelete(salesOrder: SalesOrderListItemView): void {
    if (!salesOrder.canDelete || this.isRowActionInFlight) {
      return;
    }
    this.rowActionErrorKey = '';
    this.pendingDeleteOrder = salesOrder;
    this.isDeleteConfirmationOpen = true;
  }

  private requestArchive(salesOrder: SalesOrderListItemView): void {
    if (salesOrder.status === 'Cancelled' || this.isRowActionInFlight) {
      return;
    }
    this.rowActionErrorKey = '';
    this.pendingArchiveOrder = salesOrder;
    this.isArchiveConfirmationOpen = true;
  }

  private openOrderWithQuickAction(
    salesOrder: SalesOrderListItemView,
    quickAction: 'invoice' | 'shipment',
  ): void {
    if (salesOrder.status !== 'Confirmed' || this.isRowActionInFlight) {
      return;
    }
    void this.router.navigate(
      ['/companies', this.companyId, 'crm', 'sales-orders', salesOrder.id],
      { queryParams: { quickAction } },
    );
  }

  private applyFilters(): void {
    this.request.filters = this.buildFilters(this.filterModel);
    this.activeFilterCount = this.countActiveFilters(this.filterModel);
    this.request.page = 1;
    this.isLoading = true;
    this.loadSalesOrders();
  }

  private buildFilters(model: SalesOrdersFilterModel): Filter[] {
    const filters: Filter[] = [];

    if (model.statusFilter) {
      filters.push(this.toFilter(SALES_ORDERS_FILTER_FIELDS.Status, model.statusFilter));
    }
    if (model.typeFilter) {
      filters.push(this.toFilter(SALES_ORDERS_FILTER_FIELDS.Type, model.typeFilter));
    }
    if (model.sourceFilter) {
      filters.push(this.toFilter(SALES_ORDERS_FILTER_FIELDS.Source, model.sourceFilter));
    }

    return filters;
  }

  private toFilter(field: string, value: string): Filter {
    return { field, operator: 'eq', value, values: [] };
  }

  private countActiveFilters(model: SalesOrdersFilterModel): number {
    let count = 0;
    if (model.statusFilter) { count++; }
    if (model.typeFilter) { count++; }
    if (model.sourceFilter) { count++; }
    return count;
  }

  private coerceStatusFilter(value: string): SalesOrdersStatusFilter {
    const allowed: SalesOrdersStatusFilter[] = ['Draft', 'New', 'Confirmed', 'Cancelled', 'archived', 'all'];
    return (allowed as string[]).includes(value) ? (value as SalesOrdersStatusFilter) : '';
  }

  private coerceTypeFilter(value: string): SalesOrdersTypeFilter {
    return isInventoryItemType(value) ? value : '';
  }

  private coerceSourceFilter(value: string): SalesOrdersSourceFilter {
    return value === 'bc' || value === 'local' ? value : '';
  }

  private readPersistedViewMode(companyId: string): CrmViewMode {
    try {
      return this.viewPreferencesService.getSalesOrderViewMode(companyId);
    } catch {
      return 'table';
    }
  }

  private readPersistedColumnVisibility(
    companyId: string,
  ): SalesOrdersTableColumnVisibility {
    try {
      const stored: CrmColumnVisibilityMap =
        this.viewPreferencesService.getSalesOrderColumnVisibility(companyId);
      return mergeSalesOrdersColumnVisibility(stored);
    } catch {
      return buildDefaultSalesOrdersColumnVisibility();
    }
  }

  private loadSalesOrders(): void {
    if (!this.companyId) {
      return;
    }

    this.errorMessageKey = '';
    this.salesOrders$ = this.salesOrderService
      .getSalesOrdersPaged(this.companyId, this.request)
      .pipe(
        map((page) => ({
          ...page,
          items: page.items.map((salesOrder) => this.mapSalesOrderToListItemView(salesOrder)),
        })),
        tap(() => {
          this.isLoading = false;
        }),
        catchError(() => {
          this.errorMessageKey = 'COMPONENTS.SALES_ORDERS.ERRORS.LOAD_FAILED';
          this.isLoading = false;
          return EMPTY;
        }),
        shareReplay(1),
      );
  }

  private mapSalesOrderToListItemView(salesOrder: CompanySalesOrderOverview): SalesOrderListItemView {
    const status = normalizeSalesOrderStatus(salesOrder.status);
    const type: InventoryItemType | null = salesOrder.type ?? null;
    const shortDescription = salesOrder.shortDescription?.trim() ?? '';
    const organizationTitle = (salesOrder.organizationTitle ?? '').trim();
    const contactName = (salesOrder.contactName ?? '').trim();
    const customerType = salesOrder.customerType ?? null;
    const responsibleName = (salesOrder.responsibleUserFullName ?? '').trim();
    const source: SalesOrderSource = salesOrder.businessCentralId ? 'bc' : 'local';
    const currencyCode = (salesOrder.currencyCode ?? '').trim();
    const canDelete = status === 'Draft';
    const canDuplicate = true;
    const canArchive = status !== 'Cancelled';
    const canIssueInvoice = status === 'Confirmed';
    const canCreateShipment = status === 'Confirmed';

    const clientTitle =
      customerType === 'organization'
        ? organizationTitle || '—'
        : customerType === 'contact'
          ? contactName || '—'
          : '—';

    const customerTypeLabelKey =
      customerType === 'organization'
        ? 'COMPONENTS.SALES_ORDERS.CLIENT_TYPE.ORGANIZATION'
        : customerType === 'contact'
          ? 'COMPONENTS.SALES_ORDERS.CLIENT_TYPE.CONTACT'
          : '';

    return {
      id: salesOrder.id,
      number: salesOrder.number || '—',
      customerNumber: salesOrder.customerNumber || '—',
      status,
      statusLabelKey: getSalesOrderStatusLabelKey(status),
      statusBadgeClass: `sales-order-status-badge sales-order-status-badge--${status.toLowerCase()}`,
      shortDescription: shortDescription || '—',
      hasShortDescription: shortDescription.length > 0,
      type,
      typeLabelKey: type ? getInventoryItemTypeLabelKey(type) : '',
      typeBadgeClass: type ? `sales-order-type-badge sales-order-type-badge--${type.toLowerCase()}` : '',
      hasType: type !== null,
      clientOrganizationTitle: organizationTitle || '—',
      clientContactName: contactName || '—',
      hasClient: organizationTitle !== '' || contactName !== '',
      customerType,
      clientTitle,
      customerTypeLabelKey,
      hasCustomerType: customerType !== null,
      responsibleName: responsibleName || '—',
      hasResponsible: responsibleName !== '',
      source,
      sourceLabelKey:
        source === 'bc'
          ? 'COMPONENTS.SALES_ORDERS.TABLE.SOURCE_BC'
          : 'COMPONENTS.SALES_ORDERS.TABLE.SOURCE_LOCAL',
      sourceBadgeClass: `sales-order-source-badge sales-order-source-badge--${source}`,
      orderDate: salesOrder.orderDate ?? '',
      createdAt: salesOrder.createdAt ?? '',
      postingDate: salesOrder.postingDate ?? '',
      requestedDeliveryDate: salesOrder.requestedDeliveryDate ?? '',
      amountExcludingTax: salesOrder.totals.amountExcludingTax,
      totalTaxAmount: salesOrder.totals.totalTaxAmount,
      amountIncludingTax: salesOrder.totals.amountIncludingTax,
      currencyCode,
      hasCurrencyCode: currencyCode.length > 0,
      hasCustomerNumber: !!salesOrder.customerNumber,
      canDuplicate,
      canDelete,
      actionMenuItems: [
        { id: 'open', labelKey: 'COMPONENTS.SALES_ORDERS.ACTIONS.OPEN', icon: 'open' },
        {
          id: 'issue-invoice',
          labelKey: 'COMPONENTS.SALES_ORDERS.ACTIONS.ISSUE_INVOICE',
          icon: 'invoice',
          disabled: !canIssueInvoice,
        },
        {
          id: 'create-shipment',
          labelKey: 'COMPONENTS.SALES_ORDERS.ACTIONS.CREATE_SHIPMENT',
          icon: 'shipment',
          disabled: !canCreateShipment,
        },
        {
          id: 'duplicate',
          labelKey: 'COMPONENTS.SALES_ORDERS.ACTIONS.DUPLICATE',
          icon: 'duplicate',
          disabled: !canDuplicate,
        },
        {
          id: 'archive',
          labelKey: 'COMPONENTS.SALES_ORDERS.ACTIONS.ARCHIVE',
          icon: 'archive',
          dividerBefore: true,
          disabled: !canArchive,
        },
        {
          id: 'delete',
          labelKey: 'COMPONENTS.SALES_ORDERS.ACTIONS.DELETE',
          icon: 'delete',
          danger: true,
          disabled: !canDelete,
        },
      ],
    };
  }
}
